import {
  createFileRoute,
  redirect,
  useSearch,
  Navigate,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useState, ReactNode } from 'react';
import { useGetDepartmentPath } from '@/hooks/useGetDepartmentPath';
import { FormattedMessage } from 'react-intl';
import Header from '@/components/Header/Header';
import { Loading } from '@/components/Loading';
import { DepartmentTree } from '@/components/Departments/DepartmentTree';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { DataNotFound } from '@/components/DataNotFound';
import { useGetCurrentDepartmentTree } from '@/api/trafikportalen';
import {
  getAccessibleDepartmentIds,
  getWriteAccessDepartmentIds,
} from '@/helpers/departmentPermissions';
import { IconEdit } from '@/icons';
import { DocumentHead } from '@/components/core/DocumentHead';
import {
  checkUserPermissions,
  isTenantProfile,
} from '@/components/Auth/checkUserPermissions';

export const Route = createFileRoute('/$tenantId/_layout/departments/')({
  component: Departments,
  beforeLoad: ({ params: { tenantId } }) => {
    if (isTenantProfile()) {
      throw redirect({ to: '/$tenantId', params: { tenantId } });
    }
  },
  validateSearch: (search: { departmentId: string }) => {
    return {
      departmentId: search.departmentId as string,
    };
  },
});

function Departments() {
  const { tenantId } = Route.useParams();
  const navigate = useNavigate();
  const { departmentId } = useSearch({ strict: false });

  const { data: departmentTree, isLoading } = useGetCurrentDepartmentTree();
  const findInitialDepartment = useGetDepartmentPath();

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    string | null
  >(null);

  const isMobile = useBreakpoint(768);

  useEffect(() => {
    if (!departmentTree) return;

    if (departmentId) {
      setSelectedDepartmentId(departmentId);
      return;
    }

    const initialDepartment = findInitialDepartment(
      departmentTree,
      undefined,
      'editable',
    );
    if (initialDepartment.selectedId) {
      setSelectedDepartmentId(initialDepartment.selectedId);
      return;
    }

    const accessibleIds = getAccessibleDepartmentIds();
    if (accessibleIds.length > 0) {
      setSelectedDepartmentId(accessibleIds[0]);
      return;
    }

    if (initialDepartment.paths[0]?.[0]) {
      setSelectedDepartmentId(initialDepartment.paths[0][0]);
    }
  }, [departmentTree, departmentId, findInitialDepartment]);

  const icons = checkUserPermissions({ permission: 'organization:editor' })
    ? {}
    : getWriteAccessDepartmentIds().reduce(
        (acc, departmentId) => {
          acc[departmentId] = <IconEdit />;
          return acc;
        },
        {} as Record<string, ReactNode>,
      );

  if (isLoading) {
    return <Loading />;
  }

  if (!departmentTree || !selectedDepartmentId) {
    return (
      <DataNotFound
        description={
          <FormattedMessage defaultMessage="Inga avdelningar hittades" />
        }
        buttonTitle={<FormattedMessage defaultMessage="Gå till start" />}
        onClick={() => navigate({ to: '/$tenantId', params: { tenantId } })}
      />
    );
  }

  if (!isMobile && selectedDepartmentId) {
    return (
      <Navigate
        to="/$tenantId/departments/$departmentId/$view"
        params={{
          tenantId,
          departmentId: selectedDepartmentId,
          view: 'information',
        }}
      />
    );
  }

  return (
    <>
      <DocumentHead
        titleKey="page.departments.title"
        descriptionKey="page.departments.description"
      />
      <Header title={<FormattedMessage defaultMessage="Organisation" />} />
      <div className="flex overflow-auto">
        {selectedDepartmentId && (
          <aside className="relative flex size-full flex-col md:border-r">
            <DepartmentTree
              mobileView={true}
              selectedDepartmentId={selectedDepartmentId}
              onChange={(department) => {
                navigate({
                  to: '/$tenantId/departments/$departmentId/$view',
                  params: {
                    tenantId,
                    departmentId: department.id,
                    view: 'information',
                  },
                  replace: false,
                });
              }}
              enabledDepartments={getAccessibleDepartmentIds()}
              icons={icons}
            />
          </aside>
        )}
      </div>
    </>
  );
}
